import React, { useState, useEffect } from "react";

import {
  Box,
  Container,
  Flex,
  Grid,
  Group,
  Paper,
  Radio,
  Stack,
  Button,
  Checkbox,
  Progress,
  Slider,
  Switch,
  Title,
  Tooltip,
  Text,
  Pagination,
} from "@mantine/core";

import {
  CustomAiRobot,
  CustomToaster,
  CustomDatasetTitleCard,
  CustomModal,
  CustomSiderAvatarCard,
  CustomConditionalRow,
  CustomPlayPauseStop,
} from "@components/custom";

import { DatasetSidePanel } from "@/src/pages/datasets/dataset-side-panel";
import { toast } from "sonner";

import { AccordionStylesheet } from "./stylesheet-sections/accordion";
import { AiDrawer } from "@components/drawers/ai-drawer";
import { BadgesAndChipsStylesheet } from "./stylesheet-sections/badges-and-chips";
import { ButtonsStylesheet } from "./stylesheet-sections/buttons";
import { DrawerStylesheet } from "./stylesheet-sections/drawer";
import { FormFieldsStylesheet } from "./stylesheet-sections/form-fields";
import { StepperStylesheet } from "./stylesheet-sections/stepper";
import { TableStylesheet } from "./stylesheet-sections/table";
import { TabsStylesheet } from "./stylesheet-sections/tabs";
import { TypographyStylesheet } from "./stylesheet-sections/typography";

import "@scss/globals.scss";
import styles from "@scss/pages-styles/stylesheet.module.scss";

export const Styles: React.FC = () => {
  useEffect(() => {
    document.title = "Cwiggo Console Stylesheet";
  }, []);

  const [opened, setModalOpened] = useState(false);

  const handleClick = () => {
    toast.success("The mostest perfectest toast");
  };

  return (
    <Container
      size="xl"
      py="xl"
      px={{ base: "sm", sm: "md" }}
      className={styles.container}
    >
      <Box className={styles.spacer}></Box>
      <Title order={1} mb="xl">
        Mantine v8 Components Stylesheet
      </Title>
      <Paper unstyled p={{ base: "sm", sm: "md" }} mb="xl">
        <TypographyStylesheet />
      </Paper>
      <Grid mb="xl">
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Paper p={{ base: "sm", sm: "md" }} shadow="sm" h="100%">
            <ButtonsStylesheet />
            <Tooltip label="Tooltip">
              <Button>Button with tooltip</Button>
            </Tooltip>
          </Paper>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 6 }}>
          <Paper p={{ base: "sm", sm: "md" }} shadow="sm" h="100%">
            <BadgesAndChipsStylesheet />
            <CustomPlayPauseStop />
          </Paper>
        </Grid.Col>
      </Grid>
      <Grid mb="xl">
        <Grid.Col span={12}>
          <Paper p={{ base: "sm", sm: "md" }} shadow="sm">
            <FormFieldsStylesheet />
          </Paper>
        </Grid.Col>
      </Grid>
      <Grid mb="xl">
        <Grid.Col span={12}>
          <Paper p={{ base: "sm", sm: "md" }} shadow="sm">
            <Title order={1} mb="xl">
              Additional Components
            </Title>
            <Title mb="xl">Checkboxes</Title>
            <Group pt="xl" mb="xl" wrap="wrap">
              <Stack mb="md" mr="lg">
                <Checkbox label="Checkbox1" />
                <Checkbox label="Checkbox2" defaultChecked={true} />
              </Stack>
              <Stack mb="md" mr="lg">
                <Checkbox variant="sky" label="Checkbox1" />
                <Checkbox
                  variant="sky"
                  label="Checkbox2"
                  defaultChecked={true}
                />
              </Stack>
            </Group>
            <Title>Switches</Title>
            <Group pt="xl" mb="xl" wrap="wrap">
              <Stack mb="md" mr="lg">
                <Switch label="Switch" />
                <Switch label="Switch" defaultChecked={true} />
              </Stack>
              <Stack mb="md" mr="lg">
                <Switch label="Switch" variant="sky" />
                <Switch label="Switch" defaultChecked={true} variant="sky" />
              </Stack>
            </Group>
            <Title>Radio Buttons & Groups</Title>
            <Group pt="xl" mb="xl" wrap="wrap">
              <Radio.Group label="Radio group" mb="md" mr="lg">
                <Group wrap="wrap">
                  <Radio value="1" label="Option 1" />
                  <Radio value="2" label="Option 2" />
                  <Radio value="3" label="Option 3" />
                </Group>
              </Radio.Group>
              <Radio.Group label="Radio group" mb="md">
                <Group wrap="wrap">
                  <Radio variant="sky" value="1" label="Option 1" />
                  <Radio variant="sky" value="2" label="Option 2" />
                  <Radio variant="sky" value="3" label="Option 3" />
                </Group>
              </Radio.Group>
            </Group>
            <Title mb="xl">Sliders</Title>
            {/*//I've changed the default value to be size="sm" because it looks better. You can override it here with a prop though if you want*/}
            <Slider mb="md" defaultValue={10} />{" "}
            <Slider variant="sky" mb="md" defaultValue={20} />
            <Slider variant="carrot" mb="md" defaultValue={30} />
            <Slider variant="flamingo" mb="md" defaultValue={40} />
            <Title order={1} my="xl">
              Progress Bars
            </Title>
            {/*//I've changed the default value to be size="xs" because it looks better. You can override it here with a prop though if you want*/}
            <Progress mb="md" value={50} />
            <Progress variant="sky" mb="md" value={60} />
            <Progress variant="carrot" mb="md" value={70} />
            <Progress variant="flamingo" mb="md" value={80} />
          </Paper>
        </Grid.Col>
      </Grid>
      <Grid mb="xl">
        <Grid.Col span={12}>
          <Paper p={{ base: "sm", sm: "md" }} shadow="sm">
            <Title order={1} my="xl">
              Tabs and Accordions
            </Title>
            <Grid align="top" my="lg">
              <Grid.Col span={{ base: 12, lg: 8 }}>
                <TabsStylesheet />
              </Grid.Col>
              <Grid.Col span={{ base: 12, lg: 4 }}>
                <AccordionStylesheet />
              </Grid.Col>
            </Grid>
          </Paper>
        </Grid.Col>
      </Grid>
      <Grid mb="xl">
        <Grid.Col span={12}>
          <Paper p={{ base: "sm", sm: "md" }} shadow="sm">
            <Title order={1} my="xl">
              Table
            </Title>
            <TableStylesheet />
          </Paper>
          <Paper p={{ base: "sm", sm: "md" }} shadow="sm" my="xl">
            <Title order={1} my="xl">
              Stepper
            </Title>
            <Box my={50}>
              <StepperStylesheet />
            </Box>
          </Paper>

          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Paper p={{ base: "sm", sm: "md" }} shadow="sm" my="xl">
                <Title order={1} my="xl">
                  Avatar Card
                </Title>
                <CustomSiderAvatarCard />
              </Paper>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Paper p={{ base: "sm", sm: "md" }} shadow="sm" my="xl">
                <Title order={1} my="xl">
                  Robots
                </Title>
                <Flex
                  gap="md"
                  wrap="wrap"
                  justify={{ base: "center", sm: "flex-start" }}
                >
                  <CustomAiRobot hover scale={1} />
                  <CustomAiRobot variant="smile" scale={1} />
                  <CustomAiRobot variant="think" scale={1} />
                  <CustomAiRobot variant="error" scale={1} />
                </Flex>
              </Paper>
            </Grid.Col>
          </Grid>

          <Paper p={{ base: "sm", sm: "md" }} shadow="sm" my="xl">
            <DrawerStylesheet />
            <CustomModal
              opened={opened}
              onClose={() => setModalOpened(false)}
              centered
            >
              <CustomModal.Body>
                <Title order={3} mb={10}>
                  Hey, you!
                </Title>
                <Text>Do you know that Cwiggo is awesome?</Text>
              </CustomModal.Body>
              <CustomModal.Footer>
                <Flex gap="md" justify="flex-end" align="center" wrap="wrap">
                  <Button size="sm" variant="carrot">
                    Now I do!
                  </Button>
                  <Button size="sm">I know!</Button>
                </Flex>
              </CustomModal.Footer>
            </CustomModal>
            <Group wrap="wrap">
              <Button onClick={() => setModalOpened(true)}>Open Modal</Button>
            </Group>
            <Group mt="xl" wrap="wrap">
              <Button onClick={handleClick}>Show Toast</Button>
            </Group>
            <CustomToaster position="top-right" />
          </Paper>
        </Grid.Col>
      </Grid>{" "}
      <Paper p={{ base: "sm", sm: "md" }} shadow="sm" my="xl">
        <Box my="md">
          <CustomDatasetTitleCard
            title="Test Live Dataset"
            elapsedTime="02h 22m 22s"
            variant="live"
          />
        </Box>
        <Box my="md">
          <CustomDatasetTitleCard
            title="Test Upcoming Dataset"
            variant="upcoming"
          />
        </Box>
        <Box my="md">
          <CustomDatasetTitleCard
            title="Test Replaying Dataset"
            variant="replaying"
          />
        </Box>
        <Box my="md">
          <CustomDatasetTitleCard
            title="Test Completed Dataset"
            variant="completed"
          />
        </Box>
      </Paper>
      <Paper p={{ base: "sm", sm: "md" }} shadow="sm" my="xl">
        <Box my="md" className={styles.datasetSidePanelContainer}>
          <DatasetSidePanel variant="replaying" />
        </Box>
        <Pagination
          total={10}
          value={1}
          onChange={(page) => console.log(`Page changed to: ${page}`)}
        ></Pagination>
      </Paper>
      <Paper p={{ base: "sm", sm: "md" }} shadow="sm" my="xl">
        <Box p={10} className="bg--grey200-10 br--md">
          <CustomConditionalRow />
        </Box>
      </Paper>
      <AiDrawer />
    </Container>
  );
};
